<script setup lang="ts" generic="TForm extends { Field: Component }">
  import { computed, type Component } from 'vue'
  import FormFieldMessages from './FormFieldMessages.vue'
  import { useFormAsyncMessages } from './useFormAsyncMessages'
  import type {
    FormFieldMessage,
    FormFieldMessagesAsyncHandler
  } from './form-field-messages-types'

  /**
   * Async / imperative field-message renderer for `FormField`. Rendered
   * ONLY when a consumer passes `messages` / `messages-async` — kept as a
   * separate component so the `useFormAsyncMessages` composable (which
   * subscribes to the store and promotes `variant: 'error'` messages into
   * tanstack's `errorMap.onChange` so they block submit) never runs for the
   * common, message-free field. Running it unconditionally would clobber
   * zod's `errorMap.onChange` on every field.
   *
   * Merges the tanstack field errors with the async/imperative messages,
   * de-duping by text and preferring the richer entry (which may carry a
   * `messageLong`) — mirrors the old `FormFieldLayout` behaviour.
   */
  const props = defineProps<{
    form: TForm
    name: string
    fieldErrors?: unknown[]
    messages?: FormFieldMessage[]
    messagesAsync?: FormFieldMessagesAsyncHandler
    messagesAsyncDebounceMs?: number
  }>()

  const { combinedMessages } = useFormAsyncMessages({
    form: props.form,
    name: props.name,
    messages: computed(() => props.messages),
    messagesAsync: computed(() => props.messagesAsync),
    messagesAsyncDebounceMs: computed(() => props.messagesAsyncDebounceMs)
  })

  const merged = computed<FormFieldMessage[]>(() => {
    const richErrorByText = new Map<string, FormFieldMessage>()
    for (const m of combinedMessages.value) {
      if (m.variant === 'error') richErrorByText.set(m.message, m)
    }
    const errorMessages: FormFieldMessage[] = []
    const taken = new Set<string>()
    for (const e of props.fieldErrors ?? []) {
      if (e === undefined || e === null) continue
      const msg =
        typeof e === 'string' ? e : (e as { message?: string }).message
      if (!msg || taken.has(msg)) continue
      taken.add(msg)
      errorMessages.push(
        richErrorByText.get(msg) ?? { variant: 'error', message: msg }
      )
    }
    const remaining = combinedMessages.value.filter(
      (m) => !(m.variant === 'error' && taken.has(m.message))
    )
    return [...errorMessages, ...remaining]
  })
</script>

<template>
  <FormFieldMessages v-if="merged.length > 0" :messages="merged" />
</template>
